import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT_STAGES } from "@/lib/crm";
import { toast } from "sonner";
import { format } from "date-fns";
import { AddContactDialog } from "@/components/admin/AddContactDialog";
import { SortableHead, useSortedRows, toggleSort, type SortState } from "@/components/admin/SortableHead";

type ContactSortKey = "name" | "email" | "organisation" | "source" | "stage" | "created_at";

export interface Contact {
  id: string; name: string; email: string; organisation: string | null;
  source: string | null; situation_type: string | null; urgency: string | null;
  message: string; phone: string | null; role: string | null;
  stage: string; owner_id: string | null; created_at: string;
}

export function ContactsTable({ sourceFilter }: { sourceFilter?: string }) {
  const [rows, setRows] = useState<Contact[]>([]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [selected, setSelected] = useState<Contact | null>(null);
  const [notes, setNotes] = useState<{ id: string; body: string; created_at: string }[]>([]);
  const [newNote, setNewNote] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [sort, setSort] = useState<SortState<ContactSortKey> | null>({ key: "created_at", dir: "desc" });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Contact | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    let q = supabase.from("contacts").select("*").order("created_at", { ascending: false }).limit(500);
    if (sourceFilter) q = q.eq("source", sourceFilter);
    const { data, error } = await q;
    if (error) { toast.error(error.message); return; }
    setRows(data as Contact[]);
  };

  useEffect(() => { load(); }, [sourceFilter]);

  useEffect(() => {
    if (!selected) { setNotes([]); setEditing(false); setEditForm(null); return; }
    setEditing(false);
    setEditForm(selected);
    supabase.from("contact_notes").select("id,body,created_at").eq("contact_id", selected.id).order("created_at", { ascending: false })
      .then(({ data }) => setNotes(data ?? []));
  }, [selected]);

  const filtered = useMemo(() => rows.filter(r => {
    const s = search.trim().toLowerCase();
    const matchSearch = !s || [r.name, r.email, r.organisation].some(v => v?.toLowerCase().includes(s));
    const matchStage = stageFilter === "all" || r.stage === stageFilter;
    return matchSearch && matchStage;
  }), [rows, search, stageFilter]);

  const sorted = useSortedRows(filtered, sort, (r, k) => (r as any)[k]);

  const exportCsv = () => {
    const header = ["name","email","organisation","source","stage","situation","urgency","created_at"];
    const lines = [header.join(",")].concat(filtered.map(r => header.map(h => {
      const v = (r as any)[h] ?? "";
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `contacts-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const updateContact = async (patch: { stage?: any; owner_id?: string | null }) => {
    if (!selected) return;
    const { error } = await supabase.from("contacts").update(patch).eq("id", selected.id);
    if (error) { toast.error(error.message); return; }
    setSelected({ ...selected, ...patch });
    setRows(rs => rs.map(r => r.id === selected.id ? { ...r, ...patch } : r));
  };

  const addNote = async () => {
    if (!selected || !newNote.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data, error } = await supabase.from("contact_notes")
      .insert({ contact_id: selected.id, body: newNote.trim(), author_id: user.id })
      .select().single();
    if (error) { toast.error(error.message); return; }
    setNotes(n => [data as any, ...n]);
    setNewNote("");
  };

  const saveEdits = async () => {
    if (!editForm || !selected) return;
    setSaving(true);
    const patch = {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      organisation: editForm.organisation?.trim() || null,
      phone: editForm.phone?.trim() || null,
      role: editForm.role?.trim() || null,
      source: editForm.source || null,
      situation_type: editForm.situation_type || null,
      urgency: editForm.urgency || null,
      message: editForm.message,
    };
    const { error } = await supabase.from("contacts").update(patch).eq("id", selected.id);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    const updated = { ...selected, ...patch } as Contact;
    setSelected(updated);
    setRows(rs => rs.map(r => r.id === selected.id ? updated : r));
    setEditing(false);
    toast.success("Contact updated");
  };

  const createDeal = async () => {
    if (!selected) return;
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("deals").insert({
      contact_id: selected.id,
      title: `${selected.organisation ?? selected.name} — opportunity`,
      owner_id: user?.id ?? null,
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Deal created");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search name, email, org…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {CONTACT_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={() => setAddOpen(true)}>Add contact</Button>
        <Button variant="outline" onClick={exportCsv}>Export CSV</Button>
      </div>
      <AddContactDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultSource={(sourceFilter as any) ?? "contact_page"}
        lockSource={!!sourceFilter}
        onCreated={load}
      />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead sortKey="name" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Name</SortableHead>
              <SortableHead sortKey="email" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Email</SortableHead>
              <SortableHead sortKey="organisation" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Organisation</SortableHead>
              <SortableHead sortKey="source" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Source</SortableHead>
              <SortableHead sortKey="stage" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Stage</SortableHead>
              <SortableHead sortKey="created_at" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Created</SortableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map(r => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelected(r)}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-muted-foreground">{r.email}</TableCell>
                <TableCell>{r.organisation ?? "—"}</TableCell>
                <TableCell><Badge variant="outline">{r.source ?? "—"}</Badge></TableCell>
                <TableCell><Badge>{r.stage}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{format(new Date(r.created_at), "d MMM yyyy")}</TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No contacts</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between gap-2">
                  <SheetTitle>{selected.name}</SheetTitle>
                  {editing ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditForm(selected); }} disabled={saving}>Cancel</Button>
                      <Button size="sm" onClick={saveEdits} disabled={saving}>Save</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Edit</Button>
                  )}
                </div>
              </SheetHeader>
              <div className="space-y-4 mt-4 text-sm">
                {editing && editForm ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Name</Label>
                        <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Email</Label>
                        <Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Phone</Label>
                        <Input value={editForm.phone ?? ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Organisation</Label>
                        <Input value={editForm.organisation ?? ""} onChange={(e) => setEditForm({ ...editForm, organisation: e.target.value })} /></div>
                      <div className="space-y-1 col-span-2"><Label className="text-xs text-muted-foreground">Role</Label>
                        <Input value={editForm.role ?? ""} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Source</Label>
                        <Select value={editForm.source ?? ""} onValueChange={(v) => setEditForm({ ...editForm, source: v })}>
                          <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            {["homepage","contact_page","whitepaper","referral","event","cold inbound"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Urgency</Label>
                        <Select value={editForm.urgency ?? ""} onValueChange={(v) => setEditForm({ ...editForm, urgency: v })}>
                          <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            {["Active / in the next 48 hours","Upcoming / within 2 weeks","Planning ahead / no immediate deadline"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Situation</Label>
                      <Select value={editForm.situation_type ?? ""} onValueChange={(v) => setEditForm({ ...editForm, situation_type: v })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {["Litigation & Media Exposure","Regulatory / Banking Pressure","High-Value Dispute","Other High-Stakes Situation","Whitepaper Download"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Message</Label>
                      <Textarea rows={4} value={editForm.message} onChange={(e) => setEditForm({ ...editForm, message: e.target.value })} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs text-muted-foreground">Email</Label><div>{selected.email}</div></div>
                      <div><Label className="text-xs text-muted-foreground">Phone</Label><div>{selected.phone ?? "—"}</div></div>
                      <div><Label className="text-xs text-muted-foreground">Organisation</Label><div>{selected.organisation ?? "—"}</div></div>
                      <div><Label className="text-xs text-muted-foreground">Role</Label><div>{selected.role ?? "—"}</div></div>
                      <div><Label className="text-xs text-muted-foreground">Source</Label><div>{selected.source ?? "—"}</div></div>
                      <div><Label className="text-xs text-muted-foreground">Urgency</Label><div>{selected.urgency ?? "—"}</div></div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Situation</Label>
                      <div>{selected.situation_type ?? "—"}</div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Message</Label>
                      <div className="whitespace-pre-wrap bg-muted/30 p-3 rounded">{selected.message}</div>
                    </div>
                  </>
                )}
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Stage</Label>
                  <Select value={selected.stage} onValueChange={(v) => updateContact({ stage: v as any })}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONTACT_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={createDeal}>Create deal</Button>
                </div>
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-sm font-medium">Internal notes</Label>
                  <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note…" rows={2} />
                  <Button size="sm" onClick={addNote} disabled={!newNote.trim()}>Add note</Button>
                  <div className="space-y-2 mt-2">
                    {notes.map(n => (
                      <div key={n.id} className="bg-muted/30 p-2 rounded text-xs">
                        <div className="text-muted-foreground mb-1">{format(new Date(n.created_at), "d MMM yyyy HH:mm")}</div>
                        <div className="whitespace-pre-wrap">{n.body}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function AdminContacts() {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-foreground">Contacts</h1>
      <ContactsTable />
    </div>
  );
}
